import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { companiesAPI } from '../services/api';
import DataTable from '../components/DataTable';
import { useAuthStore } from '../store/authStore';
import { hasAtLeastPermission } from '../utils/moduleAccess';

export default function Companies() {
  const user = useAuthStore((state) => state.user);
  const permissions = useAuthStore((state) => state.permissions);
  const isAdminLike = user?.user_type === 'gods_eye' || user?.user_type === 'manager';
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    ruc: '',
    address: '',
    country: '',
    email: '',
    phone: '',
    is_active: true,
  });

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    setLoading(true);
    try {
      const response = await companiesAPI.getAll();
      setCompanies(response.data);
    } catch (error) {
      console.error('Error fetching companies:', error);
      toast.error(error.response?.data?.detail || 'Error fetching companies');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const canEdit = isAdminLike || hasAtLeastPermission(permissions, 'companies', 'edit');
    if (!canEdit) {
      toast.error('You do not have permission to create or edit companies');
      return;
    }
    try {
      if (editingId) {
        await companiesAPI.update(editingId, formData);
        toast.success('Company updated successfully');
        setEditingId(null);
      } else {
        await companiesAPI.create(formData);
        toast.success('Company created successfully');
      }
      setFormData({ name: '', ruc: '', address: '', country: '', email: '', phone: '', is_active: true });
      setShowForm(false);
      fetchCompanies();
    } catch (error) {
      console.error('Error saving company:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Error saving company';
      toast.error(errorMsg);
    }
  };

  const handleDelete = async (id) => {
    const canDelete = isAdminLike || hasAtLeastPermission(permissions, 'companies', 'full_access');
    if (!canDelete) {
      toast.error('You do not have permission to delete companies');
      return;
    }
    if (window.confirm('Are you sure you want to delete this company?')) {
      try {
        await companiesAPI.delete(id);
        toast.success('Company deleted successfully');
        fetchCompanies();
      } catch (error) {
        toast.error('Error deleting company');
      }
    }
  };

  const handleEdit = (company) => {
    const canEdit = isAdminLike || hasAtLeastPermission(permissions, 'companies', 'edit');
    if (!canEdit) {
      toast.error('You do not have permission to edit companies');
      return;
    }
    setFormData(company);
    setEditingId(company.id);
    setShowForm(true);
  };

  const handleAddClick = () => {
    const canEdit = isAdminLike || hasAtLeastPermission(permissions, 'companies', 'edit');
    if (!canEdit) {
      toast.error('You do not have permission to create companies');
      return;
    }
    setFormData({ name: '', ruc: '', address: '', country: '', email: '', phone: '', is_active: true });
    setEditingId(null);
    setShowForm(true);
  };

  const columns = [
    { key: 'id', label: 'ID', width: '80px', sortable: true },
    { key: 'name', label: 'Name', sortable: true },
    { key: 'ruc', label: 'RUC', sortable: true },
    { key: 'country', label: 'Country', sortable: true },
    { key: 'email', label: 'Email', sortable: true },
    { key: 'phone', label: 'Phone', sortable: true },
  ];

  const searchFields = ['name', 'ruc', 'country', 'email', 'phone'];

  return (
    <div className="container">
      <h1 className="page-title">Companies</h1>
      {(isAdminLike || hasAtLeastPermission(permissions, 'companies', 'edit')) && (
        <button className="btn btn-primary" onClick={handleAddClick}>
          Add Company
        </button>
      )}

      {showForm && (
        <div className="modal active">
          <div className="modal-content">
            <span className="close" onClick={() => { 
              setShowForm(false); 
              setEditingId(null);
              setFormData({ name: '', ruc: '', address: '', country: '', email: '', phone: '', is_active: true });
            }}>&times;</span>
            <h2>{editingId ? 'Edit Company' : 'Add Company'}</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  disabled={!!editingId}
                />
              </div>
              <div className="form-group">
                <label>RUC</label>
                <input
                  type="text"
                  value={formData.ruc}
                  onChange={(e) => setFormData({ ...formData, ruc: e.target.value })}
                  required
                  disabled={!!editingId}
                />
              </div>
              <div className="form-group">
                <label>Address</label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Phone</label>
                <input
                  type="text"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Country</label>
                <input
                  type="text"
                  value={formData.country || ''}
                  onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                  placeholder="e.g., Ecuador"
                />
              </div>
              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  />
                  Active
                </label>
              </div>
              <button type="submit" className="btn btn-success">
                {editingId ? 'Update' : 'Create'}
              </button>
            </form>
          </div>
        </div>
      )}

      {loading ? (
        <p>Loading...</p>
      ) : (
        <DataTable
          data={companies}
          columns={columns}
          searchableFields={searchFields}
          itemsPerPage={10}
          onEdit={handleEdit}
          onDelete={(company) => handleDelete(company.id)}
          title="Companies List"
        />
      )}
    </div>
  );
}
